import * as THREE from 'three';
import { panelSurfacePoint } from '../_pure/curvedPanel.js';
import { EXTRUDED_PROFILE_SURFACE_BIAS } from '../_data/frameExtent.js';
import { geometrySpecKey } from './_pure/geometrySpec.js';
import { materialSpecKey } from './_pure/materialSpec.js';
import { sectorSliceHalfHeight } from './_pure/sectorSliceSurface.js';
import { toothedBandSamples } from './_pure/toothedBand.js';
function clamp01(value) {
    if (!Number.isFinite(value))
        return 0;
    return Math.max(0, Math.min(1, value));
}
function buildToothedBandGeometry(spec) {
    const samples = toothedBandSamples(spec);
    // Face f joins profile corners f and f+1: bottom, outer, top, inner.
    const toothedFaces = spec.toothAxis === 'radial' ? [false, true, false, true] : [true, false, true, false];
    const analyticNormal = (face, angle) => face === 0 ? new THREE.Vector3(0, -1, 0)
        : face === 2 ? new THREE.Vector3(0, 1, 0)
            : new THREE.Vector3(Math.sin(angle), 0, Math.cos(angle)).multiplyScalar(face === 1 ? 1 : -1);
    const positions = [];
    const normals = [];
    const vertex = (sample, corner) => {
        const [radius, height] = sample.corners[corner];
        return { point: new THREE.Vector3(Math.sin(sample.angle) * radius, height, Math.cos(sample.angle) * radius), angle: sample.angle };
    };
    for (let index = 0; index < samples.length - 1; index += 1) {
        const from = samples[index], to = samples[index + 1];
        for (let face = 0; face < 4; face += 1) {
            const next = (face + 1) % 4;
            const quad = [vertex(from, face), vertex(from, next), vertex(to, next), vertex(to, face)];
            const outward = analyticNormal(face, (from.angle + to.angle) / 2);
            for (const triangle of [[quad[0], quad[1], quad[2]], [quad[0], quad[2], quad[3]]]) {
                const normal = new THREE.Vector3().crossVectors(triangle[1].point.clone().sub(triangle[0].point), triangle[2].point.clone().sub(triangle[0].point));
                if (normal.lengthSq() < 1e-18)
                    continue;
                if (normal.dot(outward) < 0) {
                    [triangle[1], triangle[2]] = [triangle[2], triangle[1]];
                    normal.negate();
                }
                normal.normalize();
                for (const corner of triangle) {
                    positions.push(corner.point.x, corner.point.y, corner.point.z);
                    const shading = toothedFaces[face] ? normal : analyticNormal(face, corner.angle);
                    normals.push(shading.x, shading.y, shading.z);
                }
            }
        }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
    geometry.computeBoundingSphere();
    return geometry;
}
function buildAnnularBandGeometry(spec) {
    const segments = Math.max(16, Math.round(spec.segments));
    const positions = [];
    const colors = [];
    const indices = [];
    const inner = new THREE.Color(spec.innerColor);
    const outer = new THREE.Color(spec.outerColor);
    const cutoffHalfWidth = clamp01(spec.gradientCutoffWidth) / 2;
    const cutoffCenter = clamp01(spec.gradientCutoffCenter);
    const innerPlateauT = clamp01(cutoffCenter - cutoffHalfWidth);
    const outerPlateauT = clamp01(cutoffCenter + cutoffHalfWidth);
    const radiusDelta = spec.outerRadius - spec.innerRadius;
    const radii = [
        spec.innerRadius,
        spec.innerRadius + radiusDelta * innerPlateauT,
        spec.innerRadius + radiusDelta * outerPlateauT,
        spec.outerRadius,
    ];
    const ringsPerSample = radii.length;
    for (let i = 0; i <= segments; i += 1) {
        const angle = (i / segments) * Math.PI * 2;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        radii.forEach((radius, ringIndex) => {
            positions.push(cos * radius, 0, sin * radius);
            const color = ringIndex < 2 ? inner : outer;
            colors.push(color.r, color.g, color.b);
        });
    }
    for (let i = 0; i < segments; i += 1) {
        const base = i * ringsPerSample;
        const next = base + ringsPerSample;
        for (let ringIndex = 0; ringIndex < ringsPerSample - 1; ringIndex += 1) {
            const a = base + ringIndex;
            const b = base + ringIndex + 1;
            const c = next + ringIndex;
            const d = next + ringIndex + 1;
            indices.push(a, b, d);
            indices.push(a, d, c);
        }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
}
function buildSectorSliceGeometry(spec) {
    const geometry = new THREE.BufferGeometry();
    if (spec.itemCount <= 0 || spec.outerRadius <= 0)
        return geometry;
    const slotSpan = spec.itemCount <= 1 ? Math.PI * 2 : (Math.PI * 2) / spec.itemCount;
    const span = slotSpan * (1 - Math.min(Math.max(spec.angularGapFraction, 0), 0.95));
    const segments = Math.max(1, Math.floor(spec.arcSegments));
    const positions = [];
    const indices = [];
    const halfHeight = sectorSliceHalfHeight(spec.innerRadius, spec.outerRadius, spec.outerThickness, spec.outerRadius, spec.thicknessProfile);
    if (halfHeight > 0) {
        const innerHalfHeight = spec.innerRadius > 1e-6
            ? sectorSliceHalfHeight(spec.innerRadius, spec.outerRadius, spec.outerThickness, spec.innerRadius, spec.thicknessProfile)
            : 0;
        const innerBottomHeight = 0 - innerHalfHeight;
        const point = (radius, y, index) => {
            const angle = -span / 2 + index / segments * span;
            return [Math.sin(angle) * radius, y, Math.cos(angle) * radius];
        };
        const triangle = (a, b, c) => positions.push(...a, ...b, ...c);
        for (let i = 0; i < segments; i++) {
            const innerTop = point(spec.innerRadius, innerHalfHeight, i);
            const nextInnerTop = point(spec.innerRadius, innerHalfHeight, i + 1);
            const innerBottom = point(spec.innerRadius, innerBottomHeight, i);
            const nextInnerBottom = point(spec.innerRadius, innerBottomHeight, i + 1);
            const top = point(spec.outerRadius, halfHeight, i);
            const nextTop = point(spec.outerRadius, halfHeight, i + 1);
            const bottom = point(spec.outerRadius, -halfHeight, i);
            const nextBottom = point(spec.outerRadius, -halfHeight, i + 1);
            triangle(innerTop, top, nextTop);
            triangle(innerBottom, nextBottom, bottom);
            if (spec.innerRadius > 1e-6) {
                triangle(innerTop, nextTop, nextInnerTop);
                triangle(innerBottom, nextInnerBottom, nextBottom);
            }
            if (innerHalfHeight > 0) {
                triangle(innerTop, nextInnerBottom, innerBottom);
                triangle(innerTop, nextInnerTop, nextInnerBottom);
            }
            triangle(top, bottom, nextBottom);
            triangle(top, nextBottom, nextTop);
        }
        if (span < Math.PI * 2 - 1e-6) {
            for (const [index, sign] of [[0, 1], [segments, -1]]) {
                const innerTop = point(spec.innerRadius, innerHalfHeight, index);
                const innerBottom = point(spec.innerRadius, innerBottomHeight, index);
                const top = point(spec.outerRadius, halfHeight, index);
                const bottom = point(spec.outerRadius, -halfHeight, index);
                if (sign > 0)
                    triangle(innerBottom, bottom, top);
                else
                    triangle(innerBottom, top, bottom);
                if (innerHalfHeight > 0) {
                    if (sign > 0)
                        triangle(innerBottom, top, innerTop);
                    else
                        triangle(innerBottom, innerTop, top);
                }
            }
        }
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.computeVertexNormals();
        return geometry;
    }
    const pushArc = (radius) => {
        for (let i = 0; i <= segments; i += 1) {
            const angle = -span / 2 + (i / segments) * span;
            positions.push(Math.sin(angle) * radius, 0, Math.cos(angle) * radius);
        }
    };
    if (spec.innerRadius <= 1e-6) {
        positions.push(0, 0, 0);
        pushArc(spec.outerRadius);
        for (let i = 0; i < segments; i += 1) {
            indices.push(0, i + 1, i + 2);
        }
    }
    else {
        pushArc(spec.innerRadius);
        pushArc(spec.outerRadius);
        const outerStart = segments + 1;
        for (let i = 0; i < segments; i += 1) {
            indices.push(i, outerStart + i, outerStart + i + 1);
            indices.push(i, outerStart + i + 1, i + 1);
        }
    }
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
}
function buildPanelOutlineGeometry(spec) {
    if (spec.curvatureRadius !== undefined) {
        const points = [];
        const segments = 48;
        const point = (x, y) => new THREE.Vector3(...panelSurfacePoint(x, y, spec.curvatureRadius, spec.z));
        for (let index = 0; index < segments; index++) {
            const x = -spec.width / 2 + spec.width * index / segments;
            const next = -spec.width / 2 + spec.width * (index + 1) / segments;
            for (const y of [-spec.height / 2, spec.height / 2])
                points.push(point(x, y), point(next, y));
        }
        for (const x of [-spec.width / 2, spec.width / 2])
            points.push(point(x, -spec.height / 2), point(x, spec.height / 2));
        return new THREE.BufferGeometry().setFromPoints(points);
    }
    const halfWidth = spec.width / 2;
    const halfHeight = spec.height / 2;
    const z = spec.z;
    const geometry = new THREE.BufferGeometry();
    geometry.setFromPoints([
        new THREE.Vector3(-halfWidth, -halfHeight, z),
        new THREE.Vector3(halfWidth, -halfHeight, z),
        new THREE.Vector3(halfWidth, -halfHeight, z),
        new THREE.Vector3(halfWidth, halfHeight, z),
        new THREE.Vector3(halfWidth, halfHeight, z),
        new THREE.Vector3(-halfWidth, halfHeight, z),
        new THREE.Vector3(-halfWidth, halfHeight, z),
        new THREE.Vector3(-halfWidth, -halfHeight, z),
    ]);
    return geometry;
}
function buildSectorSliceOutlineGeometry(spec) {
    const points = [];
    const halfHeight = sectorSliceHalfHeight(spec.innerRadius, spec.outerRadius, spec.outerThickness, spec.outerRadius, spec.thicknessProfile);
    const innerHalfHeight = spec.innerRadius > 1e-6
        ? sectorSliceHalfHeight(spec.innerRadius, spec.outerRadius, spec.outerThickness, spec.innerRadius, spec.thicknessProfile)
        : 0;
    const innerBottomHeight = 0 - innerHalfHeight;
    const appendArc = (radius, y) => {
        for (let index = 0; index < spec.arcSegments; index += 1) {
            const start = spec.startAngle
                + (index / spec.arcSegments) * (spec.endAngle - spec.startAngle);
            const end = spec.startAngle
                + ((index + 1) / spec.arcSegments) * (spec.endAngle - spec.startAngle);
            points.push(new THREE.Vector3(Math.sin(start) * radius, y, Math.cos(start) * radius), new THREE.Vector3(Math.sin(end) * radius, y, Math.cos(end) * radius));
        }
    };
    appendArc(spec.outerRadius, halfHeight);
    if (halfHeight > 0)
        appendArc(spec.outerRadius, -halfHeight);
    if (spec.innerRadius > 1e-6)
        appendArc(spec.innerRadius, innerHalfHeight);
    if (innerHalfHeight > 0)
        appendArc(spec.innerRadius, innerBottomHeight);
    const fullCircle = Math.abs(Math.abs(spec.endAngle - spec.startAngle) - Math.PI * 2) <= 1e-6;
    if (!fullCircle) {
        for (const angle of [spec.startAngle, spec.endAngle]) {
            const innerTop = new THREE.Vector3(Math.sin(angle) * spec.innerRadius, innerHalfHeight, Math.cos(angle) * spec.innerRadius);
            const innerBottom = new THREE.Vector3(Math.sin(angle) * spec.innerRadius, innerBottomHeight, Math.cos(angle) * spec.innerRadius);
            const top = new THREE.Vector3(Math.sin(angle) * spec.outerRadius, halfHeight, Math.cos(angle) * spec.outerRadius);
            const bottom = new THREE.Vector3(Math.sin(angle) * spec.outerRadius, -halfHeight, Math.cos(angle) * spec.outerRadius);
            points.push(innerTop, top);
            if (halfHeight > 0)
                points.push(innerBottom, bottom, top, bottom);
            if (innerHalfHeight > 0)
                points.push(innerTop, innerBottom);
        }
    }
    return new THREE.BufferGeometry().setFromPoints(points);
}
function buildExtrudedProfileGeometry(spec) {
    const isBar = spec.surface === 'bar';
    const isFloor = spec.surface === 'floor';
    const width = isBar || isFloor ? spec.innerWidth : spec.lipThickness;
    const depth = isBar ? spec.lipHeight : isFloor ? spec.floorDepth : spec.lipHeight + spec.floorDepth;
    const lateral = isBar || isFloor ? 0 : (spec.innerWidth + spec.lipThickness) / 2
        * (spec.surface === 'left-lip' ? -1 : 1);
    const normal = isFloor ? -spec.floorDepth / 2 : isBar ? spec.lipHeight / 2 + EXTRUDED_PROFILE_SURFACE_BIAS : (spec.lipHeight - spec.floorDepth) / 2;
    const geometry = new THREE.BoxGeometry(width, 1, depth);
    geometry.translate(lateral, 0, normal);
    if (spec.outline) {
        const outline = new THREE.EdgesGeometry(geometry);
        geometry.dispose();
        return outline;
    }
    return geometry;
}
function createGeometry(spec) {
    switch (spec.geometryKind) {
        case 'sphere':
            return new THREE.SphereGeometry(spec.radius, spec.widthSegments, spec.heightSegments);
        case 'panel':
            if (spec.curvatureRadius === undefined)
                return new THREE.PlaneGeometry(spec.width, spec.height);
            {
                const geometry = new THREE.PlaneGeometry(spec.width, spec.height, 48, 1);
                const positions = geometry.getAttribute('position');
                for (let index = 0; index < positions.count; index++) {
                    positions.setXYZ(index, ...panelSurfacePoint(positions.getX(index), positions.getY(index), spec.curvatureRadius));
                }
                geometry.computeVertexNormals();
                geometry.computeBoundingSphere();
                return geometry;
            }
        case 'panel-outline':
            return buildPanelOutlineGeometry(spec);
        case 'sector-slice-outline':
            return buildSectorSliceOutlineGeometry(spec);
        case 'torus': {
            const geometry = new THREE.TorusGeometry(spec.ringRadius, spec.tubeRadius, spec.radialSegments, spec.tubularSegments);
            // Align the tube with the horizontal plane shared by the ring-family layouts.
            geometry.rotateX(Math.PI / 2);
            return geometry;
        }
        case 'revolved-profile': {
            const points = [...spec.profile, spec.profile[0]].map(([radius, height]) => new THREE.Vector2(radius, height));
            return new THREE.LatheGeometry(points, spec.segments);
        }
        case 'toothed-band':
            return buildToothedBandGeometry(spec);
        case 'annular-band':
            return buildAnnularBandGeometry(spec);
        case 'sector-slice':
            return buildSectorSliceGeometry(spec);
        case 'cylinder-wall':
            // Three's cylinder places theta 0 on +Z; theta 0..PI therefore sweeps through +X.
            return new THREE.CylinderGeometry(spec.radius, spec.radius, 1, spec.arcSegments, 1, true, 0, spec.half ? Math.PI : Math.PI * 2);
        case 'connector-tube':
            return new THREE.CylinderGeometry(0.5, 0.5, 1, spec.radialSegments);
        case 'extruded-profile':
            return buildExtrudedProfileGeometry(spec);
    }
}
function createMaterial(spec) {
    const opacity = clamp01(spec.opacity);
    switch (spec.materialKind) {
        case 'basic':
            return new THREE.MeshBasicMaterial({
                color: spec.color, opacity, side: spec.doubleSided ? THREE.DoubleSide : THREE.FrontSide,
                transparent: spec.transparent, depthWrite: spec.depthWrite,
            });
        case 'standard':
            return new THREE.MeshStandardMaterial({
                color: spec.color,
                side: spec.doubleSided ? THREE.DoubleSide : THREE.FrontSide,
                transparent: opacity < 1,
                opacity,
                depthWrite: opacity >= 1,
            });
        case 'connector-tube':
            return new THREE.MeshStandardMaterial({
                color: spec.color,
                transparent: opacity < 1,
                opacity,
                depthTest: spec.depthTest,
                roughness: 0.6,
            });
        case 'vertex-color-standard':
            return new THREE.MeshStandardMaterial({
                vertexColors: true,
                side: THREE.DoubleSide,
                transparent: opacity < 1,
                opacity,
                depthWrite: opacity >= 1,
            });
        case 'line-basic':
            return new THREE.LineBasicMaterial({
                color: spec.color,
                transparent: opacity < 1,
                opacity,
                linewidth: spec.width,
            });
    }
}
/**
 * The single owner of GPU resources in the scene. Everything it produces is derived
 * from a pure spec, so callers never construct three.js objects themselves.
 */
export function createGpuResourceCache() {
    const geometryByKey = new Map();
    const materialByKey = new Map();
    const entries = new Map();
    const knownEntries = new WeakMap();
    let collectionQueued = false;
    const register = (key, resource) => {
        const entry = { key, references: 0 };
        entries.set(resource, entry);
        knownEntries.set(resource, entry);
    };
    const collectUnused = () => {
        for (const [resource, entry] of entries) {
            if (entry.references !== 0)
                continue;
            const cache = resource instanceof THREE.BufferGeometry ? geometryByKey : materialByKey;
            if (cache.get(entry.key) === resource)
                cache.delete(entry.key);
            entries.delete(resource);
            resource.dispose();
        }
    };
    const scheduleCollection = () => {
        if (collectionQueued)
            return;
        collectionQueued = true;
        queueMicrotask(() => {
            collectionQueued = false;
            collectUnused();
        });
    };
    const getGeometryByKey = (cacheKey, create) => {
        const existing = geometryByKey.get(cacheKey);
        if (existing)
            return existing;
        const geometry = create();
        geometryByKey.set(cacheKey, geometry);
        register(cacheKey, geometry);
        return geometry;
    };
    return {
        getGeometry: (spec) => getGeometryByKey(geometrySpecKey(spec), () => createGeometry(spec)),
        getPolylineGeometry: (cacheKey, points) => getGeometryByKey(cacheKey, () => {
            const geometry = new THREE.BufferGeometry();
            geometry.setFromPoints(points.map((point) => new THREE.Vector3(point[0], point[1], point[2])));
            return geometry;
        }),
        getMaterial: (spec) => {
            const cacheKey = materialSpecKey(spec);
            const existing = materialByKey.get(cacheKey);
            if (existing)
                return existing;
            const material = createMaterial(spec);
            materialByKey.set(cacheKey, material);
            register(cacheKey, material);
            return material;
        },
        retain: (resources) => {
            const retained = [...new Set(resources)];
            for (const resource of retained) {
                if (!knownEntries.has(resource))
                    throw new Error('Cannot retain a resource owned by another GPU resource cache.');
            }
            for (const resource of retained) {
                const entry = knownEntries.get(resource);
                // An interrupted render can commit after collection. Three.js dispose releases
                // GPU storage, not CPU geometry/material data; the next mount uploads it again.
                entries.set(resource, entry);
                if (resource instanceof THREE.BufferGeometry) {
                    if (!geometryByKey.has(entry.key))
                        geometryByKey.set(entry.key, resource);
                }
                else if (!materialByKey.has(entry.key))
                    materialByKey.set(entry.key, resource);
                entry.references++;
            }
            scheduleCollection();
            let released = false;
            return () => {
                if (released)
                    return;
                released = true;
                for (const resource of retained)
                    knownEntries.get(resource).references--;
                scheduleCollection();
            };
        },
        collectUnused,
        dispose: () => {
            for (const resource of entries.keys())
                resource.dispose();
            entries.clear();
            geometryByKey.clear();
            materialByKey.clear();
        },
    };
}
